import cx from "classnames";
import { FullBleedList } from "common/FullBleedList";
import { featureTypes } from "common/MapV2/featureTypes";
import { Distance, Duration, Elevation, Grade, Pace, Speed } from "common/units";
import { commonMetrics, commonNouns, fm, Msg } from "lib/localization";
import { unitMessages } from "lib/units/unitTypes";
import _ from "lodash";
import React from "react";
import { injectIntl } from "react-intl";
import { getHighlightSliceIndexes, withTRouteMetrics } from "../../TRouteMetricsContext";
import styles from "./TripMetrics.scss";

const TripMetricRow = ({ label, value, onMouseOver, onMouseOut }) => (
  <li onMouseOver={onMouseOver} onMouseOut={onMouseOut}>
    <div>{label}</div>
    <div>
      <strong>{value}</strong>
    </div>
  </li>
);

// https://ridewithgps.com/trips/1476234 is useful for testing this!
class _TripMetrics extends React.PureComponent {
  pointMetricFeature = (label, index) => {
    const { trip, selectedFeature } = this.props;
    // if we have metrics for a highlighted segment, need to pad highlightPoint
    // index out by left index of the highlightSlice
    const highlightSlice = _.get(selectedFeature, "data.highlightSlice");
    const idx = highlightSlice ? index + highlightSlice[0].left : index;

    return {
      type: featureTypes.POINT_METRIC,
      data: {
        highlightPoint: { trackPoint: trip.trackPoints[idx], index: idx },
        label,
      },
    };
  };

  render() {
    // _trip is my way of indicating that we shouldn't directly read anything
    // off the _trip object here, since it should all flow the metrics var
    // (which can either be _trip.metrics OR the highlightSlice metrics)
    const { trip: _trip, selectedFeature, onFeatureHovered, sliceMetrics, intl } = this.props;
    if (!_trip.metrics) {
      return null;
    }

    const showSegmentMetrics = getHighlightSliceIndexes(selectedFeature) && sliceMetrics;

    const metrics = showSegmentMetrics ? sliceMetrics : _trip.metrics;

    // get metric for use as a string
    const m = (path) => Math.round(_.get(metrics, path)) || "--";

    // get metric for use as a numeric input to a formatter
    const v = (path) => _.get(metrics, path, 0);

    const maxGradeStr = fm(intl, commonMetrics.maxGrade);
    const maxSpeedStr = fm(intl, commonMetrics.maxSpeed);
    const bpmStr = fm(intl, commonMetrics.bpm);
    const maxHRStr = fm(intl, commonMetrics.maxHR);
    const maxWattsStr = fm(intl, commonMetrics.maxWatts);
    const wattsStr = fm(intl, commonMetrics.watts);
    const rpmStr = fm(intl, commonMetrics.rpm);

    return (
      <div className={cx(styles.TripMetrics, showSegmentMetrics && styles.showingSegment)}>
        <h2>
          <Msg capitalized m={commonNouns.metrics} />
        </h2>
        <FullBleedList interactive={false}>
          <TripMetricRow label={fm(intl, commonMetrics.distance)} value={<Distance short value={v("distance")} />} />
          <TripMetricRow
            label={fm(intl, commonMetrics.elevation)}
            value={
              <>
                + <Elevation short value={v("ele_gain")} /> / -
                <Elevation short value={v("ele_loss")} />
              </>
            }
          />
          <Grade short value={v("grade.max")}>
            {(m) => (
              <TripMetricRow
                onMouseOver={() =>
                  onFeatureHovered(this.pointMetricFeature(`${maxGradeStr}: ${m.valueToString()}%`, v("grade.max_i")))
                }
                onMouseOut={() => onFeatureHovered(null)}
                label={maxGradeStr}
                value={m.valueToString() + "%"}
              />
            )}
          </Grade>
          <TripMetricRow label={fm(intl, commonMetrics.avgGrade)} value={<Grade short value={v("grade.avg")} />} />
          <li className={styles.hr} />

          <TripMetricRow
            label={fm(intl, commonMetrics.totalDuration)}
            value={<Duration valueOnly biggestUnits value={v("duration")} />}
          />
          <TripMetricRow
            label={fm(intl, commonMetrics.movingTime)}
            value={<Duration valueOnly biggestUnits value={v("movingTime")} />}
          />
          <TripMetricRow
            label={fm(intl, commonMetrics.stoppedTime)}
            value={<Duration valueOnly biggestUnits value={v("stoppedTime")} />}
          />
          <li className={styles.hr} />

          <Speed short bigUnits value={v("speed.max")}>
            {(m) => (
              <TripMetricRow
                onMouseOver={() =>
                  onFeatureHovered(
                    this.pointMetricFeature(`${maxSpeedStr}: ${m.valueToString()} ${m.short}`, v("speed.max_i"))
                  )
                }
                onMouseOut={() => onFeatureHovered(null)}
                label={maxSpeedStr}
                value={`${m.valueToString()} ${m.short}`}
              />
            )}
          </Speed>
          <TripMetricRow
            label={fm(intl, commonMetrics.avgSpeed)}
            value={<Speed short bigUnits value={v("speed.avg")} />}
          />
          <li className={styles.hr} />

          <TripMetricRow label={fm(intl, commonMetrics.vam)} value={`${m("vam")} ${fm(intl, unitMessages.vmh)}`} />
          {/* Why is ascentTime camelCase when all the other metrics are snake_case?? */}
          <TripMetricRow
            label={fm(intl, commonMetrics.ascentTime)}
            value={<Duration valueOnly biggestUnits value={v("ascentTime")} />}
          />
          <TripMetricRow
            label={fm(intl, commonMetrics.descentTime)}
            value={<Duration valueOnly biggestUnits value={v("descentTime")} />}
          />
          <li className={styles.hr} />

          <TripMetricRow label={fm(intl, commonMetrics.pace)} value={<Pace valueOnly value={v("pace")} />} />
          <TripMetricRow
            label={fm(intl, commonMetrics.movingPace)}
            value={<Pace valueOnly value={v("movingPace")} />}
          />
          <li className={styles.hr} />

          {metrics.hr && metrics.hr.avg && (
            <>
              {metrics.hr.max && (
                <TripMetricRow
                  onMouseOver={() =>
                    onFeatureHovered(this.pointMetricFeature(`${maxHRStr}: ${m("hr.max")} ${bpmStr}`, v("hr.max_i")))
                  }
                  onMouseOut={() => onFeatureHovered(null)}
                  label={maxHRStr}
                  value={m("hr.max") + ` ${bpmStr}`}
                />
              )}
              {/* compare with undefined here because some sensors send 0 hr */}
              {metrics.hr.min !== undefined && (
                <TripMetricRow label={fm(intl, commonMetrics.minHR)} value={m("hr.min") + ` ${bpmStr}`} />
              )}
              {metrics.hr.avg && (
                <TripMetricRow label={fm(intl, commonMetrics.avgHR)} value={m("hr.avg") + ` ${bpmStr}`} />
              )}
              <li className={styles.hr} />
            </>
          )}

          {metrics.hr_zones && (
            <>
              <TripMetricRow
                label={fm(intl, commonMetrics.hrZone, { number: 1 })}
                value={<Duration short valueOnly biggestUnits value={metrics.hr_zones["1"]} />}
              />
              <TripMetricRow
                label={fm(intl, commonMetrics.hrZone, { number: 2 })}
                value={<Duration short valueOnly biggestUnits value={metrics.hr_zones["2"]} />}
              />
              <TripMetricRow
                label={fm(intl, commonMetrics.hrZone, { number: 3 })}
                value={<Duration short valueOnly biggestUnits value={metrics.hr_zones["3"]} />}
              />
              <TripMetricRow
                label={fm(intl, commonMetrics.hrZone, { number: 4 })}
                value={<Duration short valueOnly biggestUnits value={metrics.hr_zones["4"]} />}
              />
              <TripMetricRow
                label={fm(intl, commonMetrics.hrZone, { number: 5 })}
                value={<Duration short valueOnly biggestUnits value={metrics.hr_zones["5"]} />}
              />
              <li className={styles.hr} />
            </>
          )}

          {!showSegmentMetrics && _trip.calories > 0 && (
            <TripMetricRow label={_.capitalize(fm(intl, commonMetrics.calories))} value={_trip.calories} />
          )}

          {metrics.watts && metrics.watts.avg && (
            <>
              {metrics.watts.max && (
                <TripMetricRow
                  label={maxWattsStr}
                  onMouseOver={() =>
                    onFeatureHovered(
                      this.pointMetricFeature(
                        `${maxWattsStr}: ${m("watts.max")} ${fm(intl, commonMetrics.watts)}`,
                        v("watts.max_i")
                      )
                    )
                  }
                  onMouseOut={() => onFeatureHovered(null)}
                  value={m("watts.max") + ` ${wattsStr}`}
                />
              )}
              {/* metrics calculator doesn't provide avg power estimated, that comes from the trip model
                so we need to hide this if there's a slice selected and no real power data */}
              {(metrics.watts.avg || !showSegmentMetrics) && (
                <TripMetricRow
                  label={fm(intl, metrics.watts.avg ? commonMetrics.avgWatts : commonMetrics.avgWattsEst)}
                  value={
                    metrics.watts.avg ? (
                      m("watts.avg") + ` ${wattsStr}`
                    ) : (
                      <i>
                        {Math.round(_trip.avgPowerEstimated)} {wattsStr}
                      </i>
                    )
                  }
                />
              )}
              {metrics.watts.normalized && (
                <TripMetricRow
                  label={`WR ${fm(intl, commonMetrics.power)}`}
                  value={Math.round(metrics.watts.normalized) + ` ${wattsStr}`}
                />
              )}
              {metrics.kJ && <TripMetricRow label={fm(intl, commonMetrics.work)} value={m("kJ") + " kJ"} />}
              <li className={styles.hr} />
            </>
          )}

          {metrics.cad && metrics.cad.avg && (
            <>
              {metrics.cad.max && (
                <TripMetricRow
                  label={fm(intl, commonMetrics.maxCadence)}
                  onMouseOver={() =>
                    onFeatureHovered(
                      this.pointMetricFeature(
                        `${fm(intl, commonMetrics.maxCadence)}: ${metrics.cad.max} ${rpmStr}`,
                        v("cad.max_i")
                      )
                    )
                  }
                  onMouseOut={() => onFeatureHovered(null)}
                  value={metrics.cad.max + ` ${rpmStr}`}
                />
              )}
              {metrics.cad.min && (
                <TripMetricRow label={fm(intl, commonMetrics.minCadence)} value={metrics.cad.min + ` ${rpmStr}`} />
              )}
              {metrics.cad.avg && (
                <TripMetricRow
                  label={fm(intl, commonMetrics.avgCadence)}
                  value={Math.round(metrics.cad.avg) + ` ${rpmStr}`}
                />
              )}
              <li className={styles.hr} />
            </>
          )}
        </FullBleedList>
      </div>
    );
  }
}

export const TripMetrics = withTRouteMetrics(injectIntl(_TripMetrics));
